import { sumBy } from 'es-toolkit';
import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Rating from '@mui/material/Rating';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';

import { useRouter } from 'src/routes/hooks';
import { paths, newSearchParams } from 'src/routes/paths';

import { fShortenNumber } from 'src/utils/format-number';

import { useTranslate } from 'src/locales';
import { LIMIT } from 'src/actions/reviews';

import { Iconify } from 'src/components/iconify';

import { useAuthContext, useRedirectToSignIn } from 'src/auth/hooks';

import { ReviewList } from './components/review-list';
import { ReviewNewForm } from './components/review-new-form';

// ----------------------------------------------------------------------

export function DetailsReview({ id, collection, reviewHook, sx }) {
  const router = useRouter();

  const reviewForm = useBoolean();

  const { user } = useAuthContext();

  const urlToSignIn = useRedirectToSignIn();

  const { t, currentLang } = useTranslate();

  const reviewButton = user?.id ? user?.id !== id : true;

  const {
    loading,
    ratings,
    reviews,
    loadingMore,
    totalRating,
    totalReviews,
    addNewReview,
    loadMoreReviews,
  } = reviewHook;

  const total = sumBy(ratings, (star) => star.starCount);

  const handleOpenWriteReview = () => {
    if (!user?.id) {
      router.push(urlToSignIn);
      return;
    }

    if (!user?.profileCompleted) {
      router.push(`${paths.user.account}${newSearchParams({ country: currentLang.countryName })}`);
      return;
    }

    reviewForm.onTrue();
  };

  const renderSummary = () => (
    <Stack spacing={1} alignItems="center" justifyContent="center">
      <Typography variant="subtitle2">{t('Rating')}</Typography>

      <Typography variant="h3">{totalRating}</Typography>

      <Rating readOnly value={totalRating} precision={0.1} />

      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        ({fShortenNumber(totalReviews)} {t(totalReviews === 1 ? 'review' : 'reviews')})
      </Typography>
    </Stack>
  );

  const renderProgress = () => (
    <Stack
      spacing={1.5}
      sx={[
        (theme) => ({
          py: 5,
          px: { xs: 3, md: 5 },
          borderLeft: { md: `dashed 1px ${theme.vars.palette.divider}` },
          borderRight: { md: `dashed 1px ${theme.vars.palette.divider}` },
        }),
      ]}
    >
      {ratings.map((rating) => (
        <Stack key={rating.name} direction="row" alignItems="center">
          <Rating readOnly value={rating.name} precision={0.1} size="small" />

          <LinearProgress
            color="inherit"
            variant="determinate"
            value={total ? (rating.starCount / total) * 100 : 0}
            sx={{ mx: 2, flexGrow: 1 }}
          />

          <Typography
            variant="body2"
            component="span"
            sx={{ minWidth: 48, color: 'text.secondary' }}
          >
            {fShortenNumber(rating.starCount)}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );

  const renderReviewButton = () => (
    <Stack alignItems="center" justifyContent="center">
      <Button
        size="large"
        variant="soft"
        color="inherit"
        onClick={handleOpenWriteReview}
        startIcon={<Iconify icon="solar:pen-bold" />}
      >
        {t('Write your review')}
      </Button>
    </Stack>
  );

  const renderLoading = () => (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      sx={{ color: 'text.secondary', height: 560 }}
    >
      <CircularProgress color="inherit" />
    </Stack>
  );

  return (
    <Box sx={[...(Array.isArray(sx) ? sx : [sx])]}>
      {loading ? (
        renderLoading()
      ) : (
        <>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: 'repeat(1, 1fr)', md: 'repeat(3, 1fr)' }}
            sx={{ py: { xs: 5, md: 4 } }}
          >
            {renderSummary()}
            {renderProgress()}
            {reviewButton && renderReviewButton()}
          </Box>

          <Divider sx={{ borderStyle: 'dashed' }} />

          <ReviewList
            id={id}
            uid={user?.id}
            perPage={LIMIT}
            reviews={reviews}
            reviewHook={reviewHook}
            loadingMore={loadingMore}
            totalReviews={totalReviews}
            loadMoreReviews={loadMoreReviews}
          />

          <ReviewNewForm
            id={id}
            user={user}
            collection={collection}
            open={reviewForm.value}
            addNewReview={addNewReview}
            onClose={reviewForm.onFalse}
          />
        </>
      )}
    </Box>
  );
}
